import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { FiChevronDown, FiChevronUp, FiCalendar } from "react-icons/fi";
import "./policy.css";

const cleanNewlines = (text) => {
  if (!text || typeof text !== "string") return text;
  // Convert literal '\n' string to actual newline if present
  let str = text.replace(/\\n/g, "\n");
  
  // Replace single newlines within paragraphs with spaces, preserving double newlines (\n\n) and bullet lists
  return str
    .split(/\n\s*\n/)
    .map((p) => {
      // If it contains bullet list lines (e.g. \n- or \n*), clean inside each item
      return p
        .split(/\n(?=\s*[-*•])/)
        .map((line) => line.replace(/([^\n])\n([^\n])/g, "$1 $2"))
        .join("\n");
    })
    .join("\n\n");
};

const formatText = (text) => {
  if (!text || typeof text !== "string") return text;
  const cleaned = cleanNewlines(text);
  const parts = cleaned.split(/(\*\*[\s\S]*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

export default function PolicyLayout({ data }) {
  const [openAccordionId, setOpenAccordionId] = useState(null);
  const [activeId, setActiveId] = useState(1);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isScrollingUp, setIsScrollingUp] = useState(true);
  const lastScrollY = useRef(0);
  const accordionRefs = useRef({});

  const clickedAccordionRef = useRef(null);
  const accordionTopRef = useRef(0);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Listen to scroll to toggle the back-to-top button and sidebar reveal
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }

      if (currentScrollY < lastScrollY.current) {
        setIsScrollingUp(true);
      } else if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        setIsScrollingUp(false);
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSidebarClick = (id) => {
    setActiveId(id);
    setOpenAccordionId(id);

    setTimeout(() => {
      const element = accordionRefs.current[id];
      if (element) {
        const yOffset = -120; // Offset to account for fixed header
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        window.scrollTo({
          top: y,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const toggleAccordion = (id) => {
    clickedAccordionRef.current = id;

    const element = accordionRefs.current[id];

    if (element) {
      accordionTopRef.current = element.getBoundingClientRect().top;
    }

    setOpenAccordionId(
      openAccordionId === id ? null : id
    );
  };

  useLayoutEffect(() => {
    if (!clickedAccordionRef.current) return;

    const element = accordionRefs.current[clickedAccordionRef.current];

    if (!element) return;

    const newTop = element.getBoundingClientRect().top;

    window.scrollBy(
      0,
      newTop - accordionTopRef.current
    );
  }, [openAccordionId]);

  if (!data) return null;

  return (
    <div className="policy-container">
      <aside className={`policy-sidebar`}>
        <div className="contents-title">CONTENTS</div>
        <div className="policy-nav-list">
          {data.sections.map((item) => (
            <div
              key={item.id}
              className={`policy-nav-item ${activeId === item.id ? "active" : ""}`}
              onClick={() => handleSidebarClick(item.id)}
            >
              <span className="nav-num">{String(item.id).padStart(2, "0")}</span>
              <span className="content-title">{item.title}</span>
            </div>
          ))}
        </div>
        <div className="policy-nav-select-container">
          <div
            className="policy-mobile-header"
            onClick={() => setShowMenu(!showMenu)}
            style={{ marginTop: "-20px" }}
          >
            <span>CONTENTS</span>
            {showMenu ? <FiChevronUp /> : <FiChevronDown />}
          </div>

          {showMenu && (
            <div className="policy-mobile-list">
              {data.sections.map((item) => (
                <div
                  key={item.id}
                  className="policy-nav-item"
                  onClick={() => {
                    handleSidebarClick(item.id);
                    setShowMenu(false);
                  }}
                >
                  {item.title}
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>

      <main className="policy-main">
        <h1>{data.documentTitle || data.title}</h1>
        <div className="policy-date">
          <FiCalendar color="#2563EB" /> Last Updated: {data.lastUpdated || data.updatedAt}
        </div>
        {data.importantNotice && (
          <div className="important-notice">
            {formatText(data.importantNotice)}
          </div>
        )}
        {(data.introduction || data.intro) && (
          <div className="policy-intro">
            {data.introduction && <div className="intro-top" style={{ whiteSpace: "pre-wrap" }}>{formatText(data.introduction)}</div>}
            
            {data.intro && data.intro.topText && <div className="intro-top">{formatText(data.intro.topText)}</div>}

            {data.intro && data.intro.list && data.intro.list.length > 0 && (
              <ul className="intro-list">
                {data.intro.list.map((item, index) => (
                  <li key={index} className="intro-list-item">
                    <span className="intro-list-heading">{formatText(item.heading)}</span>
                    {" – "}
                    <span className="intro-list-content">{formatText(item.content)}</span>
                  </li>
                ))}
              </ul>
            )}

            {data.intro && data.intro.bottomText && <div className="intro-bottom">{formatText(data.intro.bottomText)}</div>}
          </div>
        )}

        <div className="policy-accordions">
          {data.sections.map((item) => {
            const isOpen = openAccordionId === item.id;
            return (
              <div
                key={item.id}
                className={`accordion-item ${isOpen ? "open" : ""}`}
                ref={el => accordionRefs.current[item.id] = el}
              >
                <div
                  className="accordion-header"
                  onClick={() => toggleAccordion(item.id)}
                >
                  <div className="accordion-title-container">
                    <span className="accordion-num">{String(item.id).padStart(2, "0")}</span>
                    <span className="accordion-title">{item.title}</span>
                  </div>
                  <div className="accordion-icon">
                    <FiChevronDown />
                  </div>
                </div>
                {isOpen && (
                  <div className="accordion-content">
                    {item.content && (
                      <div style={{ whiteSpace: "pre-wrap" }}>{formatText(item.content)}</div>
                    )}
                    {item.intro && (
                      <div style={{ whiteSpace: "pre-wrap", marginBottom: "1rem" }}>{formatText(item.intro)}</div>
                    )}
                    {item.subsections && item.subsections.length > 0 && (
                      <div className="subsections">
                        {item.subsections.map((sub, idx) => (
                          <div key={idx} className="subsection" style={{ marginBottom: "1.5rem" }}>
                            {sub.title && <h4 style={{ marginBottom: "0.5rem" }}>{formatText(sub.title)}</h4>}
                            {sub.content && <div style={{ whiteSpace: "pre-wrap" }}>{formatText(sub.content)}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button className="scroll-to-top-btn" onClick={scrollToTop} aria-label="Go to top">
          <FiChevronUp className="scroll-icon" />
        </button>
      )}
    </div>
  );
}
